import axios from 'axios';
import { handleApiError } from '@/utils/errorHandler';
import { getBackendUrl } from '@/utils/environment';

// Use centralized backend URL detection (.NET backend on port 5000)
let API_BASE_URL = getBackendUrl(5000);

/**
 * Token Provider for Authentication
 * 
 * This allows the app to provide fresh JWT tokens to axios without localStorage.
 * The token provider function should be set during app initialization.
 * 
 * All deployment modes (Native Tab, Standalone, E2B) use real OIDC/JWT tokens.
 * Token provider is registered after successful authentication in all modes.
 */
let tokenProvider: (() => Promise<string>) | null = null;

export function setTokenProvider(provider: () => Promise<string>) {
  tokenProvider = provider;
}

/**
 * Subscription Provider for Multi-Tenancy
 * 
 * This allows the app to provide subscription ID to axios without localStorage.
 * The subscription provider function should be set during app initialization.
 */
let subscriptionProvider: (() => string | null) | null = null;

export function setSubscriptionProvider(provider: () => string | null) {
  subscriptionProvider = provider;
}

/**
 * Set API Base URL dynamically (for backend switching)
 * 
 * @param url - New base URL
 */
export function setApiBaseUrl(url: string) {
  API_BASE_URL = url;
  api.defaults.baseURL = url;
  console.log('[API] Base URL updated to:', url);
}

/**
 * Get current API Base URL
 */
export function getApiBaseUrl(): string {
  return API_BASE_URL;
}

/**
 * Unified API Client for Evo Workflow Automation
 * 
 * Features:
 * - Automatic authentication token injection
 * - Subscription context header (multi-tenancy)
 * - Global error handling with toast notifications
 * - Request/response logging
 * - Payload size limits (50MB)
 */
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000, // 60 second timeout
  maxContentLength: 50 * 1024 * 1024, // 50MB
  maxBodyLength: 50 * 1024 * 1024, // 50MB
});

// Add auth token and subscription context to requests
api.interceptors.request.use(async (config) => {
  // Add authentication token from provider (all modes use JWT tokens)
  if (tokenProvider) {
    try {
      const token = await tokenProvider();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('[API] Failed to get access token from provider:', error);
      // Continue without token - let the request fail with 401 if needed
    }
  } else {
    console.warn('[API] ⚠️ No token provider registered - requests may fail with 401');
  }
  
  // Add subscription context header (for multi-tenancy in all modes)
  const subscriptionId = subscriptionProvider ? subscriptionProvider() : null;
  if (subscriptionId) {
    config.headers['X-Subscription-Id'] = subscriptionId;
  } else {
    console.warn('[API] ⚠️ No subscription provider or ID available for', config.url, {
      hasTokenProvider: !!tokenProvider,
      hasSubscriptionProvider: !!subscriptionProvider
    });
  }
  
  return config;
});

// Maximum request body size allowed by the server (100MB)
const MAX_REQUEST_BODY_SIZE = 100 * 1024 * 1024;

// Global error handling interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Calculate request data size for error reporting
    const dataSize = error.config?.data ?
      (typeof error.config.data === 'string' ? error.config.data.length : JSON.stringify(error.config.data).length) : 0;

    // Log detailed error information
    if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
      console.error('[API] Network error:', {
        message: error.message,
        code: error.code,
        url: error.config?.url,
        method: error.config?.method,
        dataSize
      });

      // If we got a network error and the payload was very large, it's likely a payload-too-large error
      // Kestrel may close the connection abruptly when the request body exceeds the limit
      if (dataSize > MAX_REQUEST_BODY_SIZE) {
        console.error('[API] Request body too large - likely exceeded server limit of 100MB');
        // Create a synthetic 413 response so the error handler can display a proper message
        error.response = {
          status: 413,
          data: {
            message: 'Request body too large',
            limit: MAX_REQUEST_BODY_SIZE,
            actualSize: dataSize
          }
        };
      }
    }

    // Handle authentication errors
    if (error.response?.status === 401) {
      console.error('[API] Authentication error - token may be expired');
      // Token and subscription are managed by providers, not localStorage
      // The useIdentity hook will handle re-authentication automatically
    }

    // Handle subscription context errors
    if (error.response?.status === 403 && error.response?.data?.error === 'Access denied') {
      console.error('[API] Subscription access denied:', error.response.data);
    }

    // Handle payload too large errors
    if (error.response?.status === 413) {
      console.error('[API] Payload too large:', {
        url: error.config?.url,
        dataSize,
        limit: error.response?.data?.limit || MAX_REQUEST_BODY_SIZE
      });
    }

    // Don't show toast for errors that will be handled locally
    // Only handle unexpected errors globally
    if (!error.config?.skipGlobalErrorHandler) {
      handleApiError(error);
    }

    return Promise.reject(error);
  }
);

// Workflows API
export const workflowsApi = {
  list: async (params?: { folderId?: string; limit?: number; offset?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.folderId) queryParams.set('folderId', params.folderId);
    if (params?.limit !== undefined) queryParams.set('limit', String(params.limit));
    if (params?.offset !== undefined) queryParams.set('offset', String(params.offset));
    const qs = queryParams.toString();
    return await api.get(`/api/workflows${qs ? `?${qs}` : ''}`);
  },
  listNames: () => api.get('/api/workflows/names'), // PERFORMANCE: Lightweight endpoint for executions page
  get: async (id: string) => await api.get(`/api/workflows/${id}`),
  getById: async (id: string) => await api.get(`/api/workflows/${id}`),
  create: (data: any) => api.post('/api/workflows', data),
  update: (id: string, data: any) => api.put(`/api/workflows/${id}`, data),
  delete: (id: string) => api.delete(`/api/workflows/${id}`),
  execute: (id: string, input?: any) => api.post(`/api/workflows/${id}/execute`, {
    inputData: input,
    tier: 'sandboxed',
    triggerMode: 'manual'
  }, {
    timeout: 300000 // 5 minutes for workflow execution
  }),
  getExecutions: (id: string) => api.get(`/api/workflows/${id}/executions`),

  // DEPRECATED: Use executeUpTo with mode='isolatedTest' instead
  // testNode: (data: { nodeId?: string; nodeType: string; nodeData?: any; inputData?: any; workflowId?: string; previousNodeOutputs?: Record<string, any>; workflow?: any }) =>
  //   api.post('/api/workflows/test-node', data),

  move: (id: string, folderId: string | null) => api.put(`/api/workflows/${id}/move`, { folderId }),
  moveToTeamFolder: (id: string, teamFolderId: string) => 
    api.post(`/api/workflows/${id}/move-to-team-folder`, { teamFolderId }),
  moveToPersonalFolder: (id: string, folderId?: string) => 
    api.post(`/api/workflows/${id}/move-to-personal-folder`, { folderId }),
  duplicate: (id: string, name?: string) => api.post(`/api/workflows/${id}/duplicate`, { name }),
  toggleActive: (id: string) => api.post(`/api/workflows/${id}/toggle`),
  share: (id: string, userId: string, permission: string) => 
    api.post(`/api/workflows/${id}/share`, { userId, permission }),
  
  // Node testing and execution
  executeUpTo: (
    id: string,
    targetNodeId: string,
    workflowDefinition?: any,
    includeTarget?: boolean,
    mode?: 'upTo' | 'upToAndIncluding' | 'isolatedTest',
    previousNodeOutputs?: Record<string, any>
  ) =>
    api.post(`/api/workflows/${id}/execute-up-to`, {
      targetNodeId,
      workflowDefinition,
      includeTarget,
      mode,
      previousNodeOutputs
    }, {
      timeout: 300000 // 5 minutes for workflow execution
    }),
  
  // Version management (from api/workflows.ts)
  getVersions: async (id: string) => {
    const response = await api.get(`/api/workflows/${id}/versions`);
    return response.data.versions || [];
  },
  getVersion: async (id: string, versionNumber: number) => {
    const response = await api.get(`/api/workflows/${id}/versions/${versionNumber}`);
    return response.data.version;
  },
  createVersion: async (id: string, changeSummary?: string, tags?: string[]) => {
    const response = await api.post(`/api/workflows/${id}/versions`, { workflowId: id, changeSummary, tags });
    return response.data.version;
  },
  restoreVersion: async (id: string, versionNumber: number) => {
    const response = await api.post(`/api/workflows/${id}/versions/${versionNumber}/restore`, { workflowId: id, versionNumber });
    return response.data;
  },
};

// Executions API
export const executionsApi = {
  get: (id: string) => api.get(`/api/executions/${id}`, {
    skipGlobalErrorHandler: true  // Don't show toast for 404 when execution is not in DB yet
  } as any),
  list: (params?: {
    limit?: number;
    offset?: number;
    status?: string;
    workflowId?: string;
    triggerType?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) => api.get('/api/executions', { params }),
  // Get workflows that have executions (for filter dropdown) - only returns permitted workflows
  getWorkflowsWithExecutions: () => api.get('/api/executions/workflows-with-executions'),
  cancel: (id: string) => api.post(`/api/executions/${id}/cancel`),
  retry: (id: string) => api.post(`/api/executions/${id}/retry`),
  execute: (workflowId: string, input?: any) => api.post(`/api/workflows/${workflowId}/execute`, {
    inputData: input,
    tier: 'sandboxed',
    triggerMode: 'manual'
  }, {
    timeout: 300000 // 5 minutes for workflow execution
  }),
  getNodeExecutions: (executionId: string, includeData: boolean = false) => api.get(`/api/executions/${executionId}/nodes`, {
    params: { includeData },
    skipGlobalErrorHandler: true  // Don't show toast for 404 when execution is not in DB yet
  } as any),
  getNodeExecutionById: (executionId: string, nodeExecutionId: string) => api.get(`/api/executions/${executionId}/nodes/${nodeExecutionId}`, {
    skipGlobalErrorHandler: true  // Don't show toast for 404
  } as any),
  getLogs: (executionId: string) => api.get(`/api/executions/${executionId}/logs`, {
    skipGlobalErrorHandler: true  // Don't show toast for 404 when execution is not in DB yet
  } as any),
  getAll: (params?: { limit?: number; offset?: number; status?: string }) => 
    api.get('/api/executions', { params }),
  // State recovery endpoints
  getExecutionState: (id: string) => api.get(`/api/executions/${id}/state`, {
    skipGlobalErrorHandler: true  // Don't show toast for 404 when execution is not in DB yet
  } as any),
  getHistory: (workflowId: string, params?: { limit?: number; offset?: number }) => 
    api.get(`/api/executions/workflow/${workflowId}/history`, { 
      params,
      skipGlobalErrorHandler: true  // Don't show toast for 404 when no execution history exists
    } as any),
  
  // Cache persistence endpoints
  cacheNodeOutputs: (workflowId: string, outputs: Record<string, any>) => 
    api.post(`/api/workflow/${workflowId}/cache-node-outputs`, { outputs }),
  
  // Execution store methods (from api/executions.ts)
  loadExecutionStore: async (executionId: string) => {
    try {
      const response = await api.get(`/api/executions/${executionId}/store`);
      return response;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null; // No store found
      }
      throw error;
    }
  },
  loadLatestExecutionStore: async (workflowId: string) => {
    try {
      const response = await api.get(`/api/executions/workflow/${workflowId}/latest-store`);
      return response;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null; // No store found
      }
      throw error;
    }
  },
  clearTestCache: async (workflowId: string) => {
    try {
      await api.delete(`/api/executions/workflow/${workflowId}/test-cache`);
    } catch (error: any) {
      // Don't throw - cache clearing is not critical
    }
  },
};

// Credentials API
export const credentialsApi = {
  list: () => api.get('/api/credentials'),
  get: (id: string) => api.get(`/api/credentials/${id}`),
  create: (data: any) => api.post('/api/credentials', data),
  update: (id: string, data: any) => api.put(`/api/credentials/${id}`, data),
  delete: (id: string) => api.delete(`/api/credentials/${id}`),
  test: (id: string) => api.post(`/api/credentials/${id}/test`),
  testDirect: (data: any) => api.post('/api/credentials/test-direct', data),
  listDatabases: (data: any) => api.post('/api/credentials/list-databases', data),
  // Check if credential exists (for shared workflow validation)
  checkExists: (id: string) => api.get<{ exists: boolean; isDeleted: boolean }>(`/api/credentials/${id}/exists`),
  // Credential sharing with users
  getShares: (id: string) => api.get(`/api/credentials/${id}/shares`),
  share: (id: string, userInternalReferenceIds: string[], isSharedWithAll: boolean = false) =>
    api.post(`/api/credentials/${id}/shares`, { userInternalReferenceIds, isSharedWithAll }),
  unshare: (id: string, userInternalReferenceId: string) =>
    api.delete(`/api/credentials/${id}/shares/${userInternalReferenceId}`),
  // Credential sharing with teams
  shareWithTeams: (id: string, teamIds: string[]) =>
    api.post(`/api/credentials/${id}/team-shares`, { teamIds }),
  unshareFromTeam: (id: string, teamId: string) =>
    api.delete(`/api/credentials/${id}/team-shares/${teamId}`),

  // OAuth methods
  getOAuthStatus: (credentialId: string) => api.get(`/api/credentials/oauth/status/${credentialId}`),
  refreshOAuthToken: (credentialId: string, provider: string) =>
    api.post(`/api/credentials/oauth/${provider}/refresh/${credentialId}`),

  // General OAuth authorization URL (works for all OAuth2 credentials)
  getAuthorizationUrl: (credentialId: string) =>
    api.get(`/api/credentials/oauth/authorization-url/${credentialId}`),

  // Deprecated: Use getAuthorizationUrl instead
  getTeamsAuthorizationUrl: (credentialId: string) =>
    api.get(`/api/credentials/oauth/teams/authorization-url/${credentialId}`),

  // Export/Import
  export: (password: string) =>
    api.post('/api/credentials/export', { password }),
  importPreview: (encryptedPayload: string, password: string) =>
    api.post('/api/credentials/import/preview', { encryptedPayload, password }),
  import: (encryptedPayload: string, password: string, selectedIndices: number[]) =>
    api.post('/api/credentials/import', { encryptedPayload, password, selectedIndices }),
};

// Properties API
export const propertiesApi = {
  getAllSchemas: () => api.get('/api/properties/schemas'),
  getSchemaForNodeType: (nodeType: string) => api.get(`/api/properties/schemas/${encodeURIComponent(nodeType)}`),
  getNodeTypes: () => api.get('/api/properties/node-types'),
  validateProperties: (nodeType: string, properties: Record<string, any>) => 
    api.post('/api/properties/validate', { nodeType, properties }),
  getDefaultProperties: (nodeType: string) => 
    api.post('/api/properties/defaults', { nodeType }),
  getPropertyCategories: (nodeType: string) => 
    api.get(`/api/properties/schemas/${encodeURIComponent(nodeType)}/categories`),
  getRequiredProperties: (nodeType: string) => 
    api.get(`/api/properties/schemas/${encodeURIComponent(nodeType)}/required`),
  getOptionalProperties: (nodeType: string) => 
    api.get(`/api/properties/schemas/${encodeURIComponent(nodeType)}/optional`),
  sanitizeProperties: (nodeType: string, properties: Record<string, any>) => 
    api.post('/api/properties/sanitize', { nodeType, properties }),
};

// Templates API
export const templatesApi = {
  list: () => api.get('/api/templates'),
  get: (id: string) => api.get(`/api/templates/${id}`),
  create: (data: any) => api.post('/api/templates', data),
  update: (id: string, data: any) => api.put(`/api/templates/${id}`, data),
  delete: (id: string) => api.delete(`/api/templates/${id}`),
  use: (id: string, name: string) => api.post(`/api/templates/${id}/use`, { name }),
};

// Folders API
export const foldersApi = {
  list: () => api.get('/api/folders'),
  get: (id: string) => api.get(`/api/folders/${id}`),
  getWorkflowCount: (id: string) => api.get(`/api/folders/${id}/workflow-count`),
  create: (data: any) => api.post('/api/folders', data),
  update: (id: string, data: any) => api.put(`/api/folders/${id}`, data),
  delete: (id: string) => api.delete(`/api/folders/${id}`),
  move: (id: string, parentId: string | null) => api.put(`/api/folders/${id}/move`, { parentId }),
};

// Projects API
export const projectsApi = {
  list: () => api.get('/api/projects'),
  get: (id: string) => api.get(`/api/projects/${id}`),
  create: (data: any) => api.post('/api/projects', data),
  update: (id: string, data: any) => api.put(`/api/projects/${id}`, data),
  delete: (id: string) => api.delete(`/api/projects/${id}`),
  
  // Members
  getMembers: (id: string) => api.get(`/api/projects/${id}/members`),
  addMember: (id: string, data: any) => api.post(`/api/projects/${id}/members`, data),
  updateMember: (id: string, userId: string, data: any) => 
    api.put(`/api/projects/${id}/members/${userId}`, data),
  removeMember: (id: string, userId: string) => 
    api.delete(`/api/projects/${id}/members/${userId}`),
};

// Sharing API
export const sharingApi = {
  // Share links
  createShareLink: (workflowId: string, data?: any) => 
    api.post(`/api/sharing/workflows/${workflowId}/share`, data),
  getShareLinks: (workflowId: string) => 
    api.get(`/api/sharing/workflows/${workflowId}/shares`),
  revokeShareLink: (workflowId: string, shareId: string) => 
    api.delete(`/api/sharing/workflows/${workflowId}/shares/${shareId}`),
  getSharedWorkflow: (token: string) => 
    api.get(`/api/sharing/shared/${token}`),
  
  // Favorites
  addFavorite: (workflowId: string) => 
    api.post(`/api/sharing/workflows/${workflowId}/favorite`),
  removeFavorite: (workflowId: string) => 
    api.delete(`/api/sharing/workflows/${workflowId}/favorite`),
  getFavorites: () => 
    api.get('/api/sharing/favorites'),
  
  // Recently viewed
  getRecentlyViewed: () => 
    api.get('/api/sharing/recent'),
};

// Webhooks API
export const webhooksApi = {
  test: (workflowId: string, nodeId: string, data: any) => 
    api.post(`/api/webhooks/${workflowId}/${nodeId}`, data),
};

// Teams API
export const teamsApi = {
  list: () => api.get('/api/teams'),
  get: (id: string) => api.get(`/api/teams/${id}`),
  create: (data: any) => api.post('/api/teams', data),
  update: (id: string, data: any) => api.put(`/api/teams/${id}`, data),
  delete: (id: string) => api.delete(`/api/teams/${id}`),
  
  // Members
  getMembers: (id: string) => api.get(`/api/teams/${id}/members`),
  addMember: (id: string, data: any) => api.post(`/api/teams/${id}/members`, data),
  updateMember: (id: string, userId: string, data: any) => 
    api.put(`/api/teams/${id}/members/${userId}`, data),
  removeMember: (id: string, userId: string) => 
    api.delete(`/api/teams/${id}/members/${userId}`),
  
  // Team resources
  getProjects: (id: string) => api.get(`/api/teams/${id}/projects`),
  getWorkflows: (id: string) => api.get(`/api/teams/${id}/workflows`),

  // Knowledge base shares check
  hasKbShares: (id: string) => api.get(`/api/teams/${id}/kb-shares`),

  // Available users for team selection (excludes current user)
  getAvailableUsers: () => api.get('/api/teams/users'),
};

// Users API (deprecated - use teamsApi.getAvailableUsers instead)
export const usersApi = {
  getSubscriptionUsers: () => api.get('/api/teams/users'),
};

// Team Folders API
export const teamFoldersApi = {
  get: (id: string) => api.get(`/api/team-folders/${id}`),
  getRootFolder: (teamId: string) => api.get(`/api/team-folders/teams/${teamId}/root`),
  getStructure: (teamId: string) => api.get(`/api/team-folders/teams/${teamId}/structure`),
  getWorkflowCount: (id: string) => api.get(`/api/team-folders/${id}/workflow-count`),
  createSubFolder: (teamId: string, data: { name: string; parentId?: string }) => 
    api.post(`/api/team-folders/teams/${teamId}`, { 
      name: data.name, 
      parentFolderId: data.parentId 
    }),
  rename: (id: string, data: { newName: string }) => 
    api.put(`/api/team-folders/${id}/rename`, { newName: data.newName }),
  move: (id: string, data: { newParentId?: string }) => 
    api.put(`/api/team-folders/${id}/move`, { newParentFolderId: data.newParentId }),
  delete: (id: string) => api.delete(`/api/team-folders/${id}`),
};

// Metrics API
export const metricsApi = {
  getExecutionMetrics: (executionId: string) => 
    api.get(`/api/metrics/executions/${executionId}`),
  getWorkflowStatistics: (workflowId: string) => 
    api.get(`/api/metrics/workflows/${workflowId}`),
  getWorkflowsMetrics: (workflowIds: string[]) => 
    api.post('/api/metrics/workflows/batch', { workflowIds }),
  getLoopIterations: (executionId: string) => 
    api.get(`/api/executions/${executionId}/loop-iterations`),
};

// MCP API
export const mcpApi = {
  listTools: (config: {
    nodeId: string;
    mcpServerUrl?: string;
    transportType: 'sse' | 'stdio';
    authType?: 'none' | 'bearer' | 'header';
    authToken?: string;
    customHeaderName?: string;
    stdioCommand?: string;
    stdioArgs?: string[];
    stdioEnv?: Record<string, string>;
  }) => api.post('/api/mcp/list-tools', config),
};

// Node Options API (for dynamic field mode toggle)
export const nodeOptionsApi = {
  /**
   * Load options dynamically from a node's options provider
   * Used for data mode in mode toggle fields (e.g., Azure Blob containers, blobs, folders)
   * Supports lazy loading via continuationToken for paginated results
   */
  loadOptions: (request: {
    nodeType: string;
    method: string;
    credentialId: string;
    dependentValues?: Record<string, any>;
    continuationToken?: string;
  }) => api.post<{
    success: boolean;
    options: Array<{ value: string; label: string; description?: string }>;
    error?: string;
    hasMore?: boolean;
    continuationToken?: string;
    totalLoaded?: number;
  }>('/api/nodes/load-options', request),

  /**
   * Get list of node types that support dynamic options
   */
  getProviders: () => api.get('/api/nodes/options-providers'),
};

// Chat API (for hosted/embedded chat)
export const chatApi = {
  getEmbedCode: (workflowId: string) => 
    api.get(`/api/chat/${workflowId}/embed-code`),
  getConfig: (workflowId: string) => 
    api.get(`/api/chat/${workflowId}/config`),
  getEmbed: (workflowId: string) => 
    api.get(`/api/chat/embed/${workflowId}`),
  getVisibilityUsers: () =>
    api.get('/api/chat/visibility-users'),
};

// Library Requests API
export const libraryRequestsApi = {
  list: () => api.get('/api/library-requests'),
  create: (data: { libraryName: string; version?: string; reason: string }) => 
    api.post('/api/library-requests', data),
  review: (id: string, action: 'approve' | 'reject', reviewerNotes?: string) => 
    api.put(`/api/library-requests/${id}/${action}`, { reviewerNotes }),
};

// Import/Export API
export const importExportApi = {
  exportWorkflow: (workflowId: string) => 
    api.get(`/api/workflows/${workflowId}/export`, { responseType: 'blob' }),
  importWorkflow: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/workflows/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  exportWorkflows: (workflowIds: string[]) => 
    api.post('/api/workflows/export/batch', { workflowIds }, { responseType: 'blob' }),
};

// Schedules API
export const schedulesApi = {
  list: () => api.get('/api/schedules'),
  get: (id: string) => api.get(`/api/schedules/${id}`),
  create: (data: any) => api.post('/api/schedules', data),
  update: (id: string, data: any) => api.patch(`/api/schedules/${id}`, data),
  delete: (id: string) => api.delete(`/api/schedules/${id}`),
  toggle: (id: string, enabled: boolean) => api.post(`/api/schedules/${id}/toggle`, { enabled }),
  getWorkflowSchedules: (workflowId: string) => api.get(`/api/workflows/${workflowId}/schedules`),
  getHistory: (id: string, limit?: number) => api.get(`/api/schedules/${id}/history`, { params: { limit } }),
  refresh: () => api.post('/api/schedules/refresh'),
};

// Batch Jobs API (AI Batch processing)
export const batchJobsApi = {
  list: (params?: { status?: string; workflowId?: string; provider?: string; skip?: number; take?: number }) =>
    api.get('/api/batch-jobs', { params }),
  get: (id: string) => api.get(`/api/batch-jobs/${id}`),
  getByExecution: (executionId: string) => api.get(`/api/batch-jobs/execution/${executionId}`),
  cancel: (id: string) => api.post(`/api/batch-jobs/${id}/cancel`),
  forcePoll: (id: string) => api.post(`/api/batch-jobs/${id}/force-poll`),
};

// Feed API (Evo Feed integration)
export const feedApi = {
  send: (data: {
    correlationId: string;
    title: string;
    description: string;
    feedType?: 'information' | 'activity';
    dueDate?: string;
    links?: Array<{
      text: string;
      desktopUrl: string;
      mobileUrl?: string;
      widgetId?: string;
    }>;
    prompts?: Array<{
      text: string;
      agentId?: string;
      pluginName?: string;
    }>;
    actions?: Array<{
      actionId: string;
      backendId: string;
      text: string;
      uri: string;
      variant?: 'primary' | 'secondary' | 'destructive';
      endIcon?: string;
    }>;
    mfeId?: string;
  }) => api.post('/api/feed/send', data),
  
  update: (data: {
    correlationId: string;
    title: string;
    description: string;
    feedType?: 'information' | 'activity';
    dueDate?: string;
    links?: any[];
    prompts?: any[];
    actions?: any[];
    mfeId?: string;
  }) => api.put('/api/feed/update', data),
  
  delete: (correlationId: string) => 
    api.delete('/api/feed/delete', { data: { correlationId } }),
};

// Errors API (for error reporting)
export const errorsApi = {
  report: (errors: any[]) => api.post('/api/errors/report', { errors }),
};

// Knowledge Base API
export const knowledgeBaseApi = {
  // Collections
  getCollections: () => api.get('/api/knowledge-base/collections'),
  getCollectionsForWorkflow: (locationType: 'personal' | 'team', teamFolderId?: string) => {
    const params = new URLSearchParams({ locationType });
    if (teamFolderId) {
      params.append('teamFolderId', teamFolderId);
    }
    return api.get(`/api/knowledge-base/collections/for-workflow?${params.toString()}`);
  },
  createCollection: (data: {
    name: string;
    description?: string;
    visibility?: 'private' | 'team' | 'public';
    embeddingModel?: string;
    teamIds?: string[];
  }) => api.post('/api/knowledge-base/collections', data),
  updateCollection: (id: string, data: {
    name?: string;
    description?: string;
  }) => api.put(`/api/knowledge-base/collections/${id}`, data),
  deleteCollection: (id: string) => api.delete(`/api/knowledge-base/collections/${id}`),
  rebuildIndex: (id: string) => api.post(`/api/knowledge-base/collections/${id}/rebuild-index`),

  // Team Sharing
  getTeamShares: (collectionId: string) => api.get(`/api/knowledge-base/collections/${collectionId}/shares`),
  updateTeamShares: (collectionId: string, teamIds: string[], visibility?: 'private' | 'team' | 'public') =>
    api.put(`/api/knowledge-base/collections/${collectionId}/shares`, { teamIds, visibility }),

  // Documents
  getDocuments: (collectionId: string) => api.get(`/api/knowledge-base/collections/${collectionId}/documents`),
  uploadDocument: (
    collectionId: string,
    data: {
      name: string;
      content: string;
      sourceType?: string;
      sourceUrl?: string;
      fileType?: string;
      chunkSize?: number;
      chunkOverlap?: number;
      metadata?: string;
    },
    credentialId?: string
  ) => api.post(
    `/api/knowledge-base/collections/${collectionId}/documents`,
    data,
    credentialId ? { headers: { 'X-Credential-Id': credentialId } } : undefined
  ),
  deleteDocument: (collectionId: string, documentId: string) =>
    api.delete(`/api/knowledge-base/collections/${collectionId}/documents/${documentId}`),
  reindexDocument: (collectionId: string, documentId: string) =>
    api.post(`/api/knowledge-base/collections/${collectionId}/documents/${documentId}/reindex`),
  getDocumentContent: (collectionId: string, documentId: string) =>
    api.get(`/api/knowledge-base/collections/${collectionId}/documents/${documentId}/content`),

  // File Upload (for PDF, DOCX, etc.)
  uploadDocumentFile: (
    collectionId: string,
    file: File,
    options?: {
      name?: string;
      chunkSize?: number;
      chunkOverlap?: number;
    }
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.name) formData.append('name', options.name);
    if (options?.chunkSize) formData.append('chunkSize', options.chunkSize.toString());
    if (options?.chunkOverlap) formData.append('chunkOverlap', options.chunkOverlap.toString());

    return api.post(
      `/api/knowledge-base/collections/${collectionId}/documents/upload`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },

  // Search
  search: (
    collectionId: string,
    data: {
      query: string;
      topK?: number;
      minScore?: number;
      includeContent?: boolean;
      outputFormat?: 'full' | 'context' | 'documents';
    },
    credentialId?: string
  ) => api.post(
    `/api/knowledge-base/collections/${collectionId}/search`,
    data,
    credentialId ? { headers: { 'X-Credential-Id': credentialId } } : undefined
  ),
};

// Data Tables API (built-in storage)
export const dataTablesApi = {
  // Tables
  getTables: () => api.get('/api/data-tables'),
  getTablesForSelection: () => api.get('/api/data-tables/select-options'),
  getTable: (id: string) => api.get(`/api/data-tables/${id}`),
  createTable: (data: {
    name: string;
    description?: string;
    schema: {
      columns: Array<{
        name: string;
        type: string;
        required?: boolean;
        default?: any;
        description?: string;
      }>;
      primaryKey?: string;
    };
  }) => api.post('/api/data-tables', data),
  updateTable: (id: string, data: { name?: string; description?: string }) =>
    api.put(`/api/data-tables/${id}`, data),
  updateTableSchema: (id: string, schema: any) =>
    api.put(`/api/data-tables/${id}/schema`, { schema }),
  deleteTable: (id: string) => api.delete(`/api/data-tables/${id}`),

  // Rows
  getRows: (tableId: string, options?: {
    page?: number;
    pageSize?: number;
    filter?: string;
    orderBy?: string;
  }) => {
    const params = new URLSearchParams();
    if (options?.page) params.append('page', options.page.toString());
    if (options?.pageSize) params.append('pageSize', options.pageSize.toString());
    if (options?.filter) params.append('filter', options.filter);
    if (options?.orderBy) params.append('orderBy', options.orderBy);
    const queryString = params.toString();
    return api.get(`/api/data-tables/${tableId}/rows${queryString ? '?' + queryString : ''}`);
  },
  getRow: (tableId: string, rowId: string) =>
    api.get(`/api/data-tables/${tableId}/rows/${rowId}`),
  insertRow: (tableId: string, data: Record<string, any>) =>
    api.post(`/api/data-tables/${tableId}/rows`, { data }),
  insertRows: (tableId: string, rows: Array<Record<string, any>>) =>
    api.post(`/api/data-tables/${tableId}/rows/bulk`, { rows }),
  updateRow: (tableId: string, rowId: string, data: Record<string, any>) =>
    api.put(`/api/data-tables/${tableId}/rows/${rowId}`, { data }),
  upsertRow: (tableId: string, keyField: string, data: Record<string, any>) =>
    api.post(`/api/data-tables/${tableId}/upsert`, { keyField, data }),
  deleteRow: (tableId: string, rowId: string) =>
    api.delete(`/api/data-tables/${tableId}/rows/${rowId}`),
  bulkDeleteRows: (tableId: string, condition: string) =>
    api.delete(`/api/data-tables/${tableId}/rows`, { data: { condition } }),
  bulkUpdateRows: (tableId: string, condition: string, updateData: Record<string, any>) =>
    api.patch(`/api/data-tables/${tableId}/rows`, { condition, updateData }),
  countRows: (tableId: string, filter?: string) => {
    const params = filter ? `?filter=${encodeURIComponent(filter)}` : '';
    return api.get(`/api/data-tables/${tableId}/count${params}`);
  },
  replaceAllRows: (tableId: string, rows: Array<Record<string, any>>) =>
    api.put(`/api/data-tables/${tableId}/rows/replace-all`, { rows }),

  // Query
  query: (tableId: string, sql: string, parameters?: Record<string, any>) =>
    api.post(`/api/data-tables/${tableId}/query`, { sql, parameters }),
  validateQuery: (tableId: string, sql: string) =>
    api.post(`/api/data-tables/${tableId}/query/validate`, { sql }),

  // Sharing
  getShares: (tableId: string) => api.get(`/api/data-tables/${tableId}/shares`),
  updateShares: (tableId: string, shares: Array<{
    sharedWithType: 'user' | 'team' | 'subscription';
    sharedWithId: string;
    permission: 'read' | 'write' | 'admin';
  }>, visibility?: 'private' | 'team' | 'public') =>
    api.put(`/api/data-tables/${tableId}/shares`, { shares, visibility }),
};

// Logs API (for frontend logs)
export const logsApi = {
  send: (logs: any[]) => api.post('/api/logs', { logs }),
};

// Import Gaps API (for n8n import roadmap tracking)
export const importGapsApi = {
  report: (sessionId: string, workflowId: string | null, gaps: Array<{
    gapType: string;
    sourceNodeType: string;
    sourceNodeName: string;
    parameterName?: string;
    parameterValue?: string;
    description: string;
    originalConfig: string;
    placeholderNodeId?: string;
  }>) => api.post('/api/import-gaps/report', { sessionId, workflowId, gaps }),

  getBySession: (sessionId: string) =>
    api.get(`/api/import-gaps/sessions/${sessionId}`),

  getStats: (days: number = 30) =>
    api.get('/api/import-gaps/stats', { params: { days } }),

  getRoadmapSuggestions: (limit: number = 20, days: number = 90) =>
    api.get('/api/import-gaps/roadmap-suggestions', { params: { limit, days } }),

  getParameterGaps: (nodeType: string, limit: number = 20, days: number = 90) =>
    api.get('/api/import-gaps/parameter-gaps', { params: { nodeType, limit, days } }),
};

// Nodes API (for dynamic dropdown options)
export const nodesApi = {
  /**
   * Load options dynamically from a node method (for cascading dropdowns)
   * @param nodeType - The node type (e.g., "microsoft-sharepoint")
   * @param method - The method name to call (e.g., "GetSitesForDropdown")
   * @param credentialId - The credential ID for authenticated API calls
   * @param parameters - Additional parameters to pass to the method
   * @returns Promise with options array: { options: Array<{ value: string, label: string }> }
   */
  loadOptions: (
    nodeType: string,
    method: string,
    credentialId?: string,
    parameters?: Record<string, string>
  ) => api.post(`/api/nodes/${encodeURIComponent(nodeType)}/load-options`, {
    method,
    credentialId: credentialId || null,
    parameters: parameters || {}
  }),

  /**
   * Get list of nodes that support polling operations
   */
  getPollableNodes: () => api.get('/api/nodes/pollable'),

  /**
   * Get available models from LiteLLM proxy (uses system credential - no user credential needed)
   * @returns Promise with LoadOptionsResponse containing available models
   */
  getLiteLLMModels: () => api.get('/api/nodes/access-llm/models'),

  /**
   * Get available embedding models from LiteLLM proxy (uses system credential - no user credential needed)
   * @returns Promise with LoadOptionsResponse containing available embedding models
   */
  getLiteLLMEmbeddingModels: () => api.get('/api/nodes/access-llm/embedding-models'),
};

// Aliases for compatibility
export const workflowApi = workflowsApi; // Some files use singular 'workflowApi'
export const executionApi = executionsApi; // Some files use singular 'executionApi'

// Export api instance as both 'api' and 'apiClient' for compatibility
export const apiClient = api;
export default api;
